import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiFilter, FiX, FiCheck,
  FiHome, FiDollarSign, FiPackage, FiEye, FiImage, FiSave, FiFileText, FiMapPin,
  FiSmartphone, FiDownload, FiCopy, FiPrinter
} from 'react-icons/fi';
import QRCode from 'qrcode';
import { shedService } from '../../services/shedService';
import { dealerService } from '../../services/dealerService';
import { supabase } from '../../lib/supabase';
import { shedMaterials, shedSizes } from '../../data/sampleSheds';
import { useAuth } from '../../hooks/useAuth';
import ImageUpload from './ImageUpload';
import VideoUpload from './VideoUpload';
import CSVBulkUpload from './CSVBulkUpload';
import SalesOrderPDF from './SalesOrderPDF';

const ShedManager = () => {
  const { userRole, dealerId } = useAuth();
  const isMaster = userRole === 'master_admin';
  const [sheds, setSheds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salesOrderShed, setSalesOrderShed] = useState(null);
  const [dealerInfo, setDealerInfo] = useState(null);
  const dealerPlan = dealerInfo?.subscription_plan || 'basic';
  const hasMultiLocation = dealerPlan === 'pro' || dealerPlan === 'enterprise';
  const lotLocations = dealerInfo?.lot_locations || [];
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingShed, setEditingShed] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showCSVUpload, setShowCSVUpload] = useState(false);

  const bodyColors = [
    'Barn Red', 'White', 'Beige', 'Tan', 'Brown', 'Gray', 'Green', 'Blue', 
    'Black', 'Clay', 'Almond', 'Sage', 'Burgundy', 'Weathered Wood', 'Custom'
  ];

  const trimColors = [
    'White', 'Black', 'Brown', 'Tan', 'Green', 'Red', 'Gray', 'Almond', 'Custom'
  ];

  const doorOptions = [
    { value: 'single', label: 'Single Door' },
    { value: 'double', label: 'Double Door' },
    { value: 'single_with_side', label: 'Single Door + Side Entry' },
    { value: 'double_with_side', label: 'Double Door + Side Entry' },
    { value: 'roll_up', label: 'Roll-Up Door' },
    { value: 'garage', label: 'Garage Door' }
  ];

  const windowOptions = [
    { value: 'none', label: 'No Windows' },
    { value: '22x36_single', label: '22x36 Window (1)' },
    { value: '22x36_double', label: '22x36 Windows (2)' },
    { value: '36x36_single', label: '36x36 Double Pane (1)' },
    { value: '36x36_double', label: '36x36 Double Pane (2)' },
    { value: '36x36_quad', label: '36x36 Double Pane (4)' },
    { value: 'custom', label: 'Custom Windows' }
  ];

  const [qrShed, setQrShed] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [dealerColors, setDealerColors] = useState([]);
  const [dealerSizes, setDealerSizes] = useState([]);
  const [dealerWindowSizes, setDealerWindowSizes] = useState([]);

  const defaultShed = {
    name: '',
    brand: '',
    model: '',
    shedCategory: '',
    size: '',
    width: '',
    depth: '',
    squareFeet: 120,
    price: '',
    salePrice: '',
    depositAmount: 500,
    material: 'Wood',
    roofStyle: 'Gambrel',
    roofMaterial: 'Architectural Shingles',
    sidingType: 'LP SmartSide',
    bodyColor: '',
    trimColor: '',
    roofColor: '',
    doorColor: '',
    floorType: 'Treated Plywood',
    foundation: 'Skid',
    condition: 'New',
    status: 'available',
    inventoryType: '',
    serialNumber: '',
    stockNumber: '',
    yearBuilt: new Date().getFullYear(),
    warranty: '5-Year Limited',
    warrantyYears: 5,
    doors: [{ type: 'double', qty: 1 }],
    windows: [{ type: '22x36_double', qty: 2 }],
    hasRamp: false,
    hasShelving: false,
    loft: false,
    workbench: false,
    electrical: false,
    insulation: false,
    deliveryIncluded: true,
    setupIncluded: true,
    onSale: false,
    salePercentage: 0,
    description: '',
    features: [],
    images: [],
    videos: [],
    lotLocation: ''
  };

  const [shedForm, setShedForm] = useState(defaultShed);
  const [allDealers, setAllDealers] = useState([]);

  useEffect(() => {
    loadSheds();
    if (dealerId) {
      loadDealerInfo();
      loadDealerColors();
      loadDealerSizes();
      loadDealerWindowSizes();
    }
    if (isMaster) loadAllDealers();
  }, []);

  const loadDealerColors = async () => {
    try {
      const did = dealerId;
      if (!did) return;
      const { data, error } = await supabase
        .from('dealer_colors')
        .select('*')
        .eq('dealer_id', did)
        .eq('is_active', true)
        .order('color_name');
      if (!error && data) setDealerColors(data);
    } catch (err) {
      console.error('Failed to load dealer colors:', err);
    }
  };

  const loadDealerSizes = async () => {
    try {
      const did = dealerId;
      if (!did) return;
      const { data, error } = await supabase
        .from('dealer_sizes')
        .select('*')
        .eq('dealer_id', did)
        .eq('is_active', true)
        .order('width');
      if (!error && data) setDealerSizes(data);
    } catch (err) {
      console.error('Failed to load dealer sizes:', err);
    }
  };

  const loadDealerWindowSizes = async () => {
    try {
      const did = dealerId;
      if (!did) return;
      const { data, error } = await supabase
        .from('dealer_window_sizes')
        .select('*')
        .eq('dealer_id', did)
        .eq('is_active', true)
        .order('width_inches');
      if (!error && data) setDealerWindowSizes(data);
    } catch (err) {
      console.error('Failed to load dealer window sizes:', err);
    }
  };

  const allBodyColors = [
    ...bodyColors,
    ...dealerColors.filter(c => c.color_type === 'body' || c.color_type === 'any').map(c => c.color_name)
  ];
  const allTrimColors = [
    ...trimColors,
    ...dealerColors.filter(c => c.color_type === 'trim' || c.color_type === 'any').map(c => c.color_name)
  ];
  const allRoofColors = dealerColors.filter(c => c.color_type === 'roof' || c.color_type === 'any').map(c => c.color_name);
  const allDoorColors = [
    ...trimColors,
    ...dealerColors.filter(c => c.color_type === 'trim' || c.color_type === 'door' || c.color_type === 'any').map(c => c.color_name)
  ];

  const getDealerHex = (colorName) => {
    const found = dealerColors.find(c => c.color_name === colorName);
    return found ? found.hex_code : null;
  };

  const loadAllDealers = async () => {
    try {
      const { data, error } = await supabase
        .from('dealers')
        .select('id, company_name, stripe_connected')
        .order('company_name');
      if (!error && data) setAllDealers(data);
    } catch (err) {
      console.error('Failed to load dealers:', err);
    }
  };

  const loadDealerInfo = async () => {
    try {
      const { data } = await dealerService.getDealerById(dealerId);
      if (data) setDealerInfo(data);
    } catch (err) {
      console.error('Failed to load dealer info:', err);
    }
  };

  const loadSheds = async () => {
    setLoading(true);
    try {
      let result;
      if (isMaster) {
        result = await shedService.getAllSheds();
      } else if (dealerId) {
        result = await shedService.getShedsByDealer(dealerId);
      } else {
        result = { data: [], error: null };
      }
      if (!result.error && result.data) {
        setSheds(result.data);
      }
    } catch (err) {
      console.error('Error loading sheds:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredSheds = sheds.filter(shed => {
    const matchesSearch = !searchTerm || 
      shed.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shed.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      shed.stockNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterType || shed.shedCategory === filterType;
    return matchesSearch && matchesType;
  });

  const handleAddNew = () => {
    setEditingShed(null);
    setShedForm(defaultShed);
    setShowModal(true);
  };

  const handleEdit = (shed) => {
    setEditingShed(shed);
    setShedForm({
      ...defaultShed,
      ...shed,
      features: shed.features || []
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this shed?')) return;
    
    try {
      const { error } = await shedService.deleteShed(id);
      if (!error) {
        setSheds(sheds.filter(s => s.id !== id));
      }
    } catch (err) {
      console.error('Error deleting shed:', err);
      alert('Failed to delete shed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required color fields
    if (!shedForm.bodyColor) {
      alert('Please select a body color for this shed.');
      return;
    }
    if (!shedForm.trimColor) {
      alert('Please select a trim color for this shed.');
      return;
    }
    if (!shedForm.size) {
      alert('Please enter a size for this shed (e.g. 10x12).');
      return;
    }
    if (!shedForm.inventoryType) {
      alert('Please select an inventory type (On Lot or Virtual).');
      return;
    }
    if (!shedForm.shedCategory) {
      alert('Please select a shed category.');
      return;
    }

    // Validate lot location for pro/enterprise dealers
    if (hasMultiLocation && lotLocations.length > 0 && !shedForm.lotLocation) {
      alert('Please select a lot location for this shed.');
      return;
    }

    setSaving(true);

    try {
      // Calculate square feet from dimensions
      const formData = {
        ...shedForm,
        squareFeet: shedForm.width * shedForm.depth,
        name: shedForm.name || `${shedForm.size} ${shedForm.brand} ${shedForm.model}`.trim()
      };

      if (editingShed) {
        const { data, error } = await shedService.updateShed(editingShed.id, formData);
        if (!error && data) {
          setSheds(sheds.map(s => s.id === editingShed.id ? data : s));
          setShowModal(false);
        } else {
          alert('Failed to update shed');
        }
      } else {
        let result;
        if (!isMaster && dealerId) {
          result = await shedService.createShedForDealer(formData, dealerId, null);
        } else {
          result = await shedService.createShed(formData);
        }
        if (!result.error && result.data) {
          setSheds([result.data, ...sheds]);
          setShowModal(false);
        } else {
          const errMsg = result.error?.message || 'Unknown error';
          console.error('Shed create failed. dealerId:', dealerId, 'error:', result.error);
          if (result.error?.code === '23503') {
            alert('Your dealer account is not properly linked. Please contact your ShedX administrator to re-link your account.');
          } else {
            alert('Failed to create shed: ' + errMsg);
          }
        }
      }
    } catch (err) {
      console.error('Error saving shed:', err);
      alert('Failed to save shed');
    } finally {
      setSaving(false);
    }
  };

  const handleSizeChange = (sizeValue) => {
    const size = sizeValue.trim();
    const match = size.match(/^(\d+\.?\d*)\s*x\s*(\d+\.?\d*)$/i);
    if (match) {
      const width = parseFloat(match[1]);
      const depth = parseFloat(match[2]);
      setShedForm(prev => ({
        ...prev,
        size,
        width,
        depth,
        squareFeet: Math.round(width * depth)
      }));
    } else {
      setShedForm(prev => ({ ...prev, size }));
    }
  };

  // QR Code generation
  const handleShowQR = async (shed) => {
    setQrShed(shed);
    const shedUrl = `${window.location.origin}/shed/${shed.id}`;
    try {
      const dataUrl = await QRCode.toDataURL(shedUrl, {
        width: 400,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' }
      });
      setQrDataUrl(dataUrl);
    } catch (err) {
      console.error('QR generation failed:', err);
    }
  };

  const handleDownloadQR = () => {
    if (!qrDataUrl || !qrShed) return;
    const link = document.createElement('a');
    link.download = `shed-${qrShed.stockNumber || qrShed.id}-qr.png`;
    link.href = qrDataUrl;
    link.click();
  };

  const handleCopyLink = () => {
    if (!qrShed) return;
    const url = `${window.location.origin}/shed/${qrShed.id}`;
    navigator.clipboard.writeText(url);
    alert('Link copied to clipboard!');
  };

  const handlePrintQR = () => {
    if (!qrDataUrl || !qrShed) return;
    const lotName = qrShed.lotLocation
      ? (dealerInfo?.lot_locations || []).find(l => l.id === qrShed.lotLocation)?.name || qrShed.lotLocation
      : '';
    const lotAddr = qrShed.lotLocation
      ? (dealerInfo?.lot_locations || []).find(l => l.id === qrShed.lotLocation)?.address || ''
      : dealerInfo?.address_line1 || '';
    const phone = dealerInfo?.phone || '';
    const website = dealerInfo?.website || '';
    const dealerName = dealerInfo?.company_name || '';
    const w = window.open('', '_blank');
    w.document.write(`
      <html><head><title>QR Code - ${qrShed.name || 'Shed'}</title>
      <style>
        body{display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;font-family:sans-serif;margin:0;padding:20px}
        img{max-width:300px;margin:16px 0}
        h2{margin-bottom:4px;font-size:22px}
        .details{color:#666;margin-top:0;font-size:14px}
        .dealer-info{margin-top:20px;text-align:center;border-top:2px solid #333;padding-top:16px;width:100%;max-width:400px}
        .dealer-name{font-size:18px;font-weight:bold;margin-bottom:4px}
        .dealer-contact{font-size:15px;font-weight:600;color:#111;margin:4px 0}
        .dealer-addr{font-size:12px;color:#666;margin:2px 0}
        .url{font-size:11px;color:#999;margin-top:12px}
      </style></head>
      <body>
        <h2>${qrShed.name || qrShed.size + ' Shed'}</h2>
        <p class="details">${qrShed.size} \u2022 ${qrShed.shedCategory || qrShed.roofStyle || ''} \u2022 ${qrShed.bodyColor || ''}</p>
        <img src="${qrDataUrl}" />
        ${dealerName ? `<div class="dealer-info">
          <div class="dealer-name">${dealerName}</div>
          ${phone ? `<div class="dealer-contact">\ud83d\udcde ${phone}</div>` : ''}
          ${website ? `<div class="dealer-contact">\ud83c\udf10 ${website}</div>` : ''}
          ${lotName ? `<div class="dealer-addr">\ud83d\udccd ${lotName}${lotAddr ? ' \u2014 ' + lotAddr : ''}</div>` : ''}
          ${!lotName && lotAddr ? `<div class="dealer-addr">\ud83d\udccd ${lotAddr}</div>` : ''}
        </div>` : ''}
        <p class="url">${window.location.origin}/shed/${qrShed.id}</p>
        <script>window.onload=()=>{window.print();window.close()}<` + `/script></body></html>
    `);
    w.document.close();
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-700';
      case 'reserved': return 'bg-yellow-100 text-yellow-700';
      case 'sold': return 'bg-red-100 text-red-700';
      case 'pending': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Shed Inventory</h1>
          <p className="text-gray-600">{sheds.length} sheds in inventory</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowCSVUpload(!showCSVUpload)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
              showCSVUpload
                ? 'bg-gray-200 text-gray-700'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            <FiPackage className="w-4 h-4" />
            {showCSVUpload ? 'Back to List' : 'CSV Upload'}
          </button>
          <button
            onClick={handleAddNew}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <FiPlus className="w-5 h-5" />
            Add New Shed
          </button>
        </div>
      </div>

      {/* CSV Bulk Upload View */}
      {showCSVUpload ? (
        <CSVBulkUpload onComplete={() => { setShowCSVUpload(false); loadSheds(); }} />
      ) : (
      <>
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, brand, or stock number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
          >
            <option value="">All Types</option>
            <option value="Barn">Barn</option>
            <option value="Gable">Gable</option>
            <option value="Lean-To">Lean-To</option>
            <option value="Studio">Studio</option>
            <option value="Garden Shed">Garden Shed</option>
          </select>
        </div>
      </div>

      {/* Sheds Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm h-64 animate-pulse" />
          ))}
        </div>
      ) : filteredSheds.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <FiHome className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No sheds found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSheds.map(shed => (
            <motion.div
              key={shed.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm overflow-hidden"
            >
              {/* Image */}
              <div className="relative h-56 bg-gray-100">
                {shed.images?.[0] ? (
                  <img
                    src={shed.images[0]}
                    alt={shed.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FiHome className="w-12 h-12 text-gray-300" />
                  </div>
                )}
                <span className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${getStatusColor(shed.status)}`}>
                  {shed.status}
                </span>
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-1">{shed.name}</h3>
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                  <span>{shed.size}</span>
                  <span>•</span>
                  <span className="capitalize">{shed.shedCategory || 'N/A'}</span>
                  <span>•</span>
                  <span>{shed.material}</span>
                  {shed.inventoryType === 'virtual' && (
                    <span className="ml-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-medium rounded">VIRTUAL</span>
                  )}
                </div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    {shed.salePrice ? (
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-emerald-600">{formatPrice(shed.salePrice)}</span>
                        <span className="text-sm text-gray-400 line-through">{formatPrice(shed.price)}</span>
                      </div>
                    ) : (
                      <span className="text-lg font-bold text-emerald-600">{formatPrice(shed.price)}</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">#{shed.stockNumber}</span>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(shed)}
                    className="flex-1 flex items-center justify-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 rounded-lg transition-colors text-sm"
                  >
                    <FiEdit2 className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => window.open(`/shed/${shed.id}`, '_blank')}
                    className="flex items-center justify-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-3 rounded-lg transition-colors text-sm"
                  >
                    <FiEye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleShowQR(shed)}
                    className="flex items-center justify-center gap-1 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 py-2 px-3 rounded-lg transition-colors text-sm"
                    title="Generate QR Code"
                  >
                    <FiSmartphone className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setSalesOrderShed(shed)}
                    className="flex items-center justify-center gap-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 py-2 px-3 rounded-lg transition-colors text-sm"
                    title="Generate Sales Order PDF"
                  >
                    <FiFileText className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(shed.id)}
                    className="flex items-center justify-center gap-1 bg-red-100 hover:bg-red-200 text-red-600 py-2 px-3 rounded-lg transition-colors text-sm"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                  {isMaster && (
                    <select
                      value={shed.dealer_id || ''}
                      onChange={async (e) => {
                        const newDealerId = e.target.value || null;
                        if (!window.confirm(`Reassign this shed to a different dealer?`)) return;
                        try {
                          await supabase.from('sheds').update({ dealer_id: newDealerId }).eq('id', shed.id);
                          loadSheds();
                        } catch (err) {
                          alert('Failed to reassign: ' + err.message);
                        }
                      }}
                      className="text-[11px] px-1 py-1 border border-gray-200 rounded-lg bg-amber-50 text-gray-600 max-w-[100px] truncate"
                      title="Reassign to dealer"
                    >
                      <option value="">Reassign...</option>
                      {allDealers.map(d => (
                        <option key={d.id} value={d.id}>{d.company_name}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-50 overflow-y-auto"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-4xl my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingShed ? 'Edit Shed' : 'Add New Shed'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 max-h-[70vh] overflow-y-auto">
                {/* Basic Info */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={shedForm.name}
                        onChange={(e) => setShedForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., 10x12 Classic Barn"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                      <input
                        type="text"
                        value={shedForm.brand}
                        onChange={(e) => setShedForm(prev => ({ ...prev, brand: e.target.value }))}
                        placeholder="e.g., Heritage"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                      <input
                        type="text"
                        value={shedForm.model}
                        onChange={(e) => setShedForm(prev => ({ ...prev, model: e.target.value }))}
                        placeholder="e.g., Classic Barn"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Lot Location — Pro & Enterprise only */}
                {hasMultiLocation && lotLocations.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                      <FiMapPin className="w-5 h-5 text-emerald-600" />
                      Lot Location
                      <span className="text-xs font-normal text-red-500">*required</span>
                    </h3>
                    <select
                      value={shedForm.lotLocation || ''}
                      onChange={(e) => setShedForm(prev => ({ ...prev, lotLocation: e.target.value }))}
                      required
                      className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-emerald-500 ${
                        !shedForm.lotLocation ? 'border-amber-300 bg-amber-50' : 'border-gray-200'
                      }`}
                    >
                      <option value="">— Select which lot this shed is at —</option>
                      {lotLocations.map(loc => (
                        <option key={loc.id} value={loc.name}>
                          {loc.name}{loc.city ? ` — ${loc.city}, ${loc.state}` : ''}
                        </option>
                      ))}
                    </select>
                    {!shedForm.lotLocation && (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <FiMapPin className="w-3 h-3" />
                        You must assign this shed to a lot location
                      </p>
                    )}
                  </div>
                )}

                {/* Dealer Assignment — Master Admin only */}
                {isMaster && allDealers.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                      <FiHome className="w-5 h-5 text-emerald-600" />
                      Assign to Dealer
                    </h3>
                    <select
                      value={shedForm.dealer_id || ''}
                      onChange={(e) => setShedForm(prev => ({ ...prev, dealer_id: e.target.value || null }))}
                      className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-emerald-500 ${
                        !shedForm.dealer_id ? 'border-amber-300 bg-amber-50' : 'border-gray-200'
                      }`}
                    >
                      <option value="">— No dealer assigned —</option>
                      {allDealers.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.company_name}{d.stripe_connected ? ' ✓ Stripe Active' : ' ⚠ No Stripe'}
                        </option>
                      ))}
                    </select>
                    {!shedForm.dealer_id && (
                      <p className="text-xs text-amber-600 mt-1">Sheds without a dealer can't accept payments</p>
                    )}
                  </div>
                )}

                {/* INVENTORY TYPE — REQUIRED */}
                <div className={`mb-6 p-4 rounded-xl border-2 ${!shedForm.inventoryType ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-white'}`}>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Inventory Type <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setShedForm(prev => ({ ...prev, inventoryType: 'physical' }))}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        shedForm.inventoryType === 'physical'
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-800 shadow-sm'
                          : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      <FiHome className="w-6 h-6" />
                      <span className="font-semibold text-sm">On Lot</span>
                      <span className="text-[11px] text-center leading-tight opacity-75">Physical shed on your lot, ready for purchase</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShedForm(prev => ({ ...prev, inventoryType: 'virtual' }))}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        shedForm.inventoryType === 'virtual'
                          ? 'border-purple-500 bg-purple-50 text-purple-800 shadow-sm'
                          : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                      }`}
                    >
                      <FiPackage className="w-6 h-6" />
                      <span className="font-semibold text-sm">Virtual</span>
                      <span className="text-[11px] text-center leading-tight opacity-75">Available to order, not currently on display</span>
                    </button>
                  </div>
                  {!shedForm.inventoryType && (
                    <p className="text-xs text-amber-600 mt-2 font-medium">⚠ You must select an inventory type</p>
                  )}
                </div>

                {/* SHED CATEGORY — REQUIRED */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Shed Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={shedForm.shedCategory}
                    onChange={(e) => setShedForm(prev => ({ ...prev, shedCategory: e.target.value }))}
                    required
                    className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm ${
                      !shedForm.shedCategory ? 'border-amber-300 bg-amber-50' : 'border-gray-200'
                    }`}
                  >
                    <option value="">— Select shed category —</option>
                    <option value="Barn">Barn</option>
                    <option value="Gable">Gable</option>
                    <option value="Lean-To">Lean-To</option>
                    <option value="Studio">Studio</option>
                    <option value="Garden Shed">Garden Shed</option>
                  </select>
                  {!shedForm.shedCategory && (
                    <p className="text-xs text-amber-600 mt-1 font-medium">⚠ You must select a category</p>
                  )}
                </div>

                {/* Size & Type */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Size & Type</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Size *</label>
                      <input
                        type="text"
                        list="shedSizeOptions"
                        value={shedForm.size}
                        onChange={(e) => handleSizeChange(e.target.value)}
                        placeholder="Type or select (e.g. 10x12)"
                        required
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 ${
                          !shedForm.size ? 'border-amber-300 bg-amber-50' : 'border-gray-200'
                        }`}
                      />
                      <datalist id="shedSizeOptions">
                        {shedSizes.map(size => (
                          <option key={size.value} value={size.value}>{size.label}</option>
                        ))}
                        {dealerSizes
                          .filter(ds => !shedSizes.some(s => s.value === ds.size_label))
                          .map(ds => (
                            <option key={`ds-${ds.id}`} value={ds.size_label}>
                              {ds.size_label} ({Math.round(ds.width * ds.depth)} sq ft)
                            </option>
                          ))
                        }
                      </datalist>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Material</label>
                      <select
                        value={shedForm.material}
                        onChange={(e) => setShedForm(prev => ({ ...prev, material: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      >
                        {shedMaterials.map(material => (
                          <option key={material.value} value={material.value}>{material.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Construction Details */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Construction Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Roof Style</label>
                      <select
                        value={shedForm.roofStyle}
                        onChange={(e) => setShedForm(prev => ({ ...prev, roofStyle: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="Gambrel">Gambrel</option>
                        <option value="Gable">Gable</option>
                        <option value="Hip">Hip</option>
                        <option value="Flat">Flat</option>
                        <option value="Lean-To">Lean-To / Shed</option>
                        <option value="A-Frame">A-Frame</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Roof Material</label>
                      <select
                        value={shedForm.roofMaterial}
                        onChange={(e) => setShedForm(prev => ({ ...prev, roofMaterial: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="Standard 3 Tab Shingles">Standard 3 Tab Shingles</option>
                        <option value="Architectural Shingles">Architectural Shingles</option>
                        <option value="Metal">Metal</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Floor Type</label>
                      <select
                        value={shedForm.floorType}
                        onChange={(e) => setShedForm(prev => ({ ...prev, floorType: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="OSB">OSB</option>
                        <option value="Treated Plywood">Treated Plywood</option>
                        <option value="Regular Plywood">Regular Plywood</option>
                        <option value="Advantech">Advantech</option>
                        <option value="LP Legacy">LP Legacy</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Siding Type</label>
                      <input
                        type="text"
                        value={shedForm.sidingType}
                        onChange={(e) => setShedForm(prev => ({ ...prev, sidingType: e.target.value }))}
                        placeholder="e.g., LP SmartSide, T1-11, Vinyl"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Foundation</label>
                      <select
                        value={shedForm.foundation}
                        onChange={(e) => setShedForm(prev => ({ ...prev, foundation: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="Skid">Skid</option>
                        <option value="Concrete">Concrete</option>
                        <option value="Gravel Pad">Gravel Pad</option>
                        <option value="Pier & Beam">Pier & Beam</option>
                        <option value="None">None</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Pricing */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                      <input
                        type="number"
                        required
                        value={shedForm.price}
                        onChange={(e) => setShedForm(prev => ({ ...prev, price: parseFloat(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sale Price</label>
                      <input
                        type="number"
                        value={shedForm.salePrice || ''}
                        onChange={(e) => setShedForm(prev => ({ ...prev, salePrice: e.target.value ? parseFloat(e.target.value) : null }))}
                        placeholder="Leave empty if no sale"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Deposit Amount</label>
                      <input
                        type="number"
                        value={shedForm.depositAmount}
                        onChange={(e) => setShedForm(prev => ({ ...prev, depositAmount: parseFloat(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={shedForm.status}
                        onChange={(e) => setShedForm(prev => ({ ...prev, status: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="available">Available</option>
                        <option value="reserved">Reserved</option>
                        <option value="pending">Pending</option>
                        <option value="sold">Sold</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Colors & Options */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Colors & Options</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Main Body Color *</label>
                      <div className="flex items-center gap-2">
                        {shedForm.bodyColor && getDealerHex(shedForm.bodyColor) && (
                          <span className="w-6 h-6 rounded-full border border-gray-300 flex-shrink-0" style={{ backgroundColor: getDealerHex(shedForm.bodyColor) }} />
                        )}
                        <select
                          value={shedForm.bodyColor}
                          onChange={(e) => setShedForm(prev => ({ ...prev, bodyColor: e.target.value }))}
                          required
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 ${
                            !shedForm.bodyColor ? 'border-amber-300 bg-amber-50' : 'border-gray-200'
                          }`}
                        >
                          <option value="">— Select body color —</option>
                          {allBodyColors.map(color => (
                            <option key={color} value={color}>{color}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Trim Color *</label>
                      <div className="flex items-center gap-2">
                        {shedForm.trimColor && getDealerHex(shedForm.trimColor) && (
                          <span className="w-6 h-6 rounded-full border border-gray-300 flex-shrink-0" style={{ backgroundColor: getDealerHex(shedForm.trimColor) }} />
                        )}
                        <select
                          value={shedForm.trimColor}
                          onChange={(e) => setShedForm(prev => ({ ...prev, trimColor: e.target.value }))}
                          required
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 ${
                            !shedForm.trimColor ? 'border-amber-300 bg-amber-50' : 'border-gray-200'
                          }`}
                        >
                          <option value="">— Select trim color —</option>
                          {allTrimColors.map(color => (
                            <option key={color} value={color}>{color}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Roof Color</label>
                      <input
                        type="text"
                        value={shedForm.roofColor}
                        onChange={(e) => setShedForm(prev => ({ ...prev, roofColor: e.target.value }))}
                        placeholder="e.g., Charcoal, Black"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Door Color</label>
                      <div className="flex items-center gap-2">
                        {shedForm.doorColor && getDealerHex(shedForm.doorColor) && (
                          <span className="w-6 h-6 rounded-full border border-gray-300 flex-shrink-0" style={{ backgroundColor: getDealerHex(shedForm.doorColor) }} />
                        )}
                        <select
                          value={shedForm.doorColor}
                          onChange={(e) => setShedForm(prev => ({ ...prev, doorColor: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        >
                          <option value="">— Select door color —</option>
                          {allDoorColors.map(color => (
                            <option key={color} value={color}>{color}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Door & Window Builder */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Doors & Windows</h3>
                  
                  {/* Doors */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">Doors</label>
                      <button
                        type="button"
                        onClick={() => setShedForm(prev => ({
                          ...prev,
                          doors: [...(prev.doors || []), { type: 'single', qty: 1 }]
                        }))}
                        className="text-xs flex items-center gap-1 text-emerald-600 hover:text-emerald-700"
                      >
                        <FiPlus size={12} /> Add Door
                      </button>
                    </div>
                    <div className="space-y-2">
                      {(shedForm.doors || []).map((door, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                          <span className="text-xs text-gray-400 w-6">{idx + 1}.</span>
                          <select
                            value={door.type}
                            onChange={(e) => {
                              const updated = [...(shedForm.doors || [])];
                              updated[idx] = { ...updated[idx], type: e.target.value };
                              setShedForm(prev => ({ ...prev, doors: updated }));
                            }}
                            className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                          >
                            {doorOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          <div className="flex items-center gap-1">
                            <label className="text-xs text-gray-500">Qty</label>
                            <input
                              type="number"
                              min="1"
                              max="10"
                              value={door.qty}
                              onChange={(e) => {
                                const updated = [...(shedForm.doors || [])];
                                updated[idx] = { ...updated[idx], qty: parseInt(e.target.value) || 1 };
                                setShedForm(prev => ({ ...prev, doors: updated }));
                              }}
                              className="w-14 px-2 py-1.5 text-sm text-center border border-gray-200 rounded-lg"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (shedForm.doors || []).filter((_, i) => i !== idx);
                              setShedForm(prev => ({ ...prev, doors: updated }));
                            }}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          >
                            <FiX size={14} />
                          </button>
                        </div>
                      ))}
                      {(!shedForm.doors || shedForm.doors.length === 0) && (
                        <p className="text-xs text-gray-400 italic">No doors added — click "Add Door" above</p>
                      )}
                    </div>
                  </div>

                  {/* Windows */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">Windows</label>
                      <button
                        type="button"
                        onClick={() => setShedForm(prev => ({
                          ...prev,
                          windows: [...(prev.windows || []), { type: '22x36_double', qty: 1 }]
                        }))}
                        className="text-xs flex items-center gap-1 text-emerald-600 hover:text-emerald-700"
                      >
                        <FiPlus size={12} /> Add Window
                      </button>
                    </div>
                    <div className="space-y-2">
                      {(shedForm.windows || []).map((win, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                          <span className="text-xs text-gray-400 w-6">{idx + 1}.</span>
                          <select
                            value={win.type}
                            onChange={(e) => {
                              const updated = [...(shedForm.windows || [])];
                              updated[idx] = { ...updated[idx], type: e.target.value };
                              setShedForm(prev => ({ ...prev, windows: updated }));
                            }}
                            className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                          >
                            {windowOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                            {dealerWindowSizes.length > 0 && (
                              <optgroup label="Your Custom Sizes">
                                {dealerWindowSizes
                                  .filter(dw => !windowOptions.some(o => o.label === dw.label))
                                  .map(dw => (
                                    <option key={`dw-${dw.id}`} value={dw.label}>{dw.label}</option>
                                  ))
                                }
                              </optgroup>
                            )}
                          </select>
                          <div className="flex items-center gap-1">
                            <label className="text-xs text-gray-500">Qty</label>
                            <input
                              type="number"
                              min="1"
                              max="20"
                              value={win.qty}
                              onChange={(e) => {
                                const updated = [...(shedForm.windows || [])];
                                updated[idx] = { ...updated[idx], qty: parseInt(e.target.value) || 1 };
                                setShedForm(prev => ({ ...prev, windows: updated }));
                              }}
                              className="w-14 px-2 py-1.5 text-sm text-center border border-gray-200 rounded-lg"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = (shedForm.windows || []).filter((_, i) => i !== idx);
                              setShedForm(prev => ({ ...prev, windows: updated }));
                            }}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          >
                            <FiX size={14} />
                          </button>
                        </div>
                      ))}
                      {(!shedForm.windows || shedForm.windows.length === 0) && (
                        <p className="text-xs text-gray-400 italic">No windows added — click "Add Window" above</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Add-ons & Features */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Add-ons & Features</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { key: 'hasRamp', label: 'Ramp' },
                      { key: 'hasShelving', label: 'Shelving' },
                      { key: 'loft', label: 'Loft' },
                      { key: 'workbench', label: 'Workbench' },
                      { key: 'electrical', label: 'Electrical' },
                      { key: 'insulation', label: 'Insulation' },
                      { key: 'deliveryIncluded', label: 'Free Delivery' },
                      { key: 'setupIncluded', label: 'Free Setup' },
                    ].map(feature => (
                      <label key={feature.key} className="flex items-center gap-2 cursor-pointer p-3 border rounded-lg hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={shedForm[feature.key]}
                          onChange={(e) => { const k = feature.key; const v = e.target.checked; setShedForm(prev => ({ ...prev, [k]: v })); }}
                          className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                        />
                        <span className="text-gray-700">{feature.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Promotions & Condition */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Promotions & Condition</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          checked={shedForm.onSale}
                          onChange={(e) => setShedForm(prev => ({ ...prev, onSale: e.target.checked }))}
                          className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                        />
                        <span className="text-sm font-medium text-gray-700">On Sale / Promotion</span>
                      </label>
                      {shedForm.onSale && (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={shedForm.salePercentage || ''}
                            onChange={(e) => {
                              const pct = parseInt(e.target.value) || 0;
                              setShedForm(prev => {
                                const newSalePrice = prev.price ? Math.round(prev.price * (1 - pct/100)) : '';
                                return { ...prev, salePercentage: pct, salePrice: newSalePrice };
                              });
                            }}
                            placeholder="%"
                            className="w-20 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                          />
                          <span className="text-sm text-gray-600">% off</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                      <select
                        value={shedForm.condition}
                        onChange={(e) => setShedForm(prev => ({ ...prev, condition: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="New">New</option>
                        <option value="Like New">Like New</option>
                        <option value="Used">Used</option>
                        <option value="Refurbished">Refurbished</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Identifiers */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Identifiers</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Stock Number</label>
                      <input
                        type="text"
                        value={shedForm.stockNumber}
                        onChange={(e) => setShedForm(prev => ({ ...prev, stockNumber: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number</label>
                      <input
                        type="text"
                        value={shedForm.serialNumber}
                        onChange={(e) => setShedForm(prev => ({ ...prev, serialNumber: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Year Built</label>
                      <input
                        type="number"
                        value={shedForm.yearBuilt}
                        onChange={(e) => setShedForm(prev => ({ ...prev, yearBuilt: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
                  <textarea
                    value={shedForm.description}
                    onChange={(e) => setShedForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    placeholder="Describe the shed, its features, and condition..."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                {/* Images */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Images</h3>
                  <ImageUpload
                    images={shedForm.images}
                    onImagesChange={(imgs) => setShedForm(prev => ({ ...prev, images: imgs }))}
                  />
                </div>
              </form>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="w-4 h-4" />
                      {editingShed ? 'Update Shed' : 'Add Shed'}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </>
      )}

      {/* QR Code Modal */}
      <AnimatePresence>
        {qrShed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => { setQrShed(null); setQrDataUrl(''); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">QR Code</h3>
                <button
                  onClick={() => { setQrShed(null); setQrDataUrl(''); }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="text-center mb-4">
                <h4 className="font-medium text-gray-800">{qrShed.name || `${qrShed.size} Shed`}</h4>
                <p className="text-sm text-gray-500">{qrShed.size} • {qrShed.shedCategory || qrShed.roofStyle} • {qrShed.bodyColor}</p>
              </div>

              {qrDataUrl && (
                <div className="flex justify-center mb-6">
                  <img src={qrDataUrl} alt="QR Code" className="w-64 h-64 border rounded-lg" />
                </div>
              )}

              <div className="text-center mb-6">
                <p className="text-xs text-gray-400 break-all">{window.location.origin}/shed/{qrShed.id}</p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={handleDownloadQR}
                  className="flex flex-col items-center gap-1 p-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl transition-colors"
                >
                  <FiDownload className="w-5 h-5" />
                  <span className="text-xs font-medium">Download</span>
                </button>
                <button
                  onClick={handleCopyLink}
                  className="flex flex-col items-center gap-1 p-3 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-xl transition-colors"
                >
                  <FiCopy className="w-5 h-5" />
                  <span className="text-xs font-medium">Copy Link</span>
                </button>
                <button
                  onClick={handlePrintQR}
                  className="flex flex-col items-center gap-1 p-3 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-xl transition-colors"
                >
                  <FiPrinter className="w-5 h-5" />
                  <span className="text-xs font-medium">Print</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sales Order PDF Modal */}
      <SalesOrderPDF
        isOpen={!!salesOrderShed}
        onClose={() => setSalesOrderShed(null)}
        shed={salesOrderShed}
        dealer={null}
      />
    </div>
  );
};

export default ShedManager;
